import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Link } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { getClaims, login, setPassword } from 'shared/api/auth';
import { TextEditor } from 'shared/components/data-display/editors';
import { VARIANTS } from 'shared/constants';
import { resetPath } from 'shared/routes/auth';
import PasswordForm from 'shared/components/PasswordForm';

export default function Login() {
  const { isLoading } = useRequest();
  const { search } = useLocation();
  const { setAccessToken } = useAuth();
  const notification = useNotification();

  const [state, setState] = useState({
    password: '',
    tmpAccessToken: null,
    userId: null,
    username: '',
  });

  const {
    password,
    tmpAccessToken,
    userId,
    username,
  } = state;

  // Check if there is a password reset token in the URL.
  useEffect(() => {
    const token = new URLSearchParams(search).get('token');
    if (token) {
      const user = getClaims(token);
      setState((currentState) => ({
        ...currentState,
        tmpAccessToken: token,
        username: user.email,
        userId: user.id,
      }));
    }
  }, [search]);

  const onChange = (name) => (value) => {
    setState({
      ...state,
      [name]: value,
    });
  };

  const submit = async () => {
    try {
      const result = await login(username, password);
      const { accessToken } = result;

      const user = getClaims(accessToken);
      if (user.status === 'FORCE_RESET') {
        // Set the tmp access token to state so that the user can update their password.
        notification.pushInfo('A new password is required');
        setState({
          ...state,
          tmpAccessToken: accessToken,
          userId: user.id,
        });
      } else {
        setAccessToken(accessToken);
        notification.pushSuccess('Logged in');
      }
    } catch (error) {
      notification.pushError(error);
    }
  };

  const setNewPassword = async (newPassword) => {
    let success;
    try {
      await setPassword(tmpAccessToken, userId, newPassword);
      const result = await login(username, newPassword);
      const { accessToken } = result;
      setAccessToken(accessToken);
      notification.pushSuccess('Logged in');
      success = true;
    } catch (error) {
      notification.pushError(error);
      success = false;
    }
    return success;
  };

  return (
    tmpAccessToken
      ? (
        <PasswordForm setNewPassword={setNewPassword} disabled={isLoading} />
      )
      : (
        <Grid size={{
          xs: 10, md: 8, lg: 4, xl: 3,
        }}
        >
          <Grid size={12}>
            <TextEditor
              required
              autoComplete="username"
              setValue={onChange('username')}
              label="Username"
              value={username}
            />
          </Grid>
          <Grid size={12}>
            <TextEditor
              type="password"
              autoComplete="current-password"
              required
              setValue={onChange('password')}
              label="Password"
              value={password}
            />
          </Grid>
          <Grid
            container
            size={12}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Link href={`${resetPath}?username=${username}`}>Reset Password</Link>
            </Grid>
            <Grid>
              <Button variant={VARIANTS.contained} disabled={isLoading} onClick={submit}>
                Login
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )
  );
}
